import { useParams } from 'react-router-dom';
import { config } from "../../data/config";
import { useEffect, useState } from 'react';
import { getCookies } from "cookies-next";
import { useCookies } from 'react-cookie';
import axios from "axios";
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import BreadCrump from '../tools/breadcrump';
import HeaderOfPage from '../tools/headerOfPage';
import ImageUploader from '../tools/uploaderImage';
import TissEditor from '../Editor/TissEditor';
import QACreator from '../Qa/QaComponent';
import { Tabs, Input, Button } from 'rsuite';
import changeUrl from "../../hoc/changeur";

type LanguageCode = string;

interface MultilingualField {
    [key: string]: string;
}

// Helper function to get display name from multilingual or string
const getDisplayName = (name: any): string => {
    if (!name) return '';
    if (typeof name === 'string') return name;
    if (typeof name === 'object') {
        return name.en || name.fa || name.ku || name.ar || name.tr || '';
    }
    return String(name);
};

export default function EditPost() {
    const { t } = useTranslation();
    const { Id } = useParams();
    const [cookies] = useCookies(['token']);
    const cookie = getCookies();

    const [data, setData] = useState<any>(null);
    const [list, setListArticle] = useState<any>(null);
    const [error, setError] = useState<any>(null);
    const [key, setKey] = useState<number>(1);
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [translating, setTranslating] = useState<boolean>(false);
    const [activeLang, setActiveLang] = useState<LanguageCode>('en');
    const [languages, setLanguages] = useState<Array<{ code: LanguageCode; label: string }>>([]);

    // Translate text: try backend first, then Google fallback
    const translateText = async (text: string, fromLang: LanguageCode, toLang: LanguageCode): Promise<string> => {
        if (!text || String(text).trim() === '') return '';
        if (fromLang === toLang) return text;
        try {
            try {
                const response = await axios.post(
                    `${config.url}/v1/translate`,
                    { text: text.trim(), from: fromLang, to: toLang, format: 'text' },
                    { headers: { Authorization: 'Bearer ' + cookie['token'] } }
                );
                if (response.data?.translatedText) return response.data.translatedText;
            } catch (_) {
                // Backend not available, use Google
            }
            const response = await fetch(
                `https://translate.googleapis.com/translate_a/single?client=gtx&sl=${fromLang}&tl=${toLang}&dt=t&q=${encodeURIComponent(text.trim())}`
            );
            if (!response.ok) return '';
            const result = await response.json();
            const translated = result[0]?.map((s: any) => s?.[0] || '').join('').trim();
            return translated || text;
        } catch (_) {
            return '';
        }
    };

    const handleAutoTranslate = async () => {
        const nameText = (data.name?.[activeLang] || '').trim();
        const titleText = (data.title?.[activeLang] || '').trim();
        const descText = (data.description?.[activeLang] || '').trim();
        const keywordsText = (data.keywords?.[activeLang] || '').trim();
        if (!nameText && !titleText && !descText && !keywordsText) {
            Swal.fire({
                title: t('No content to translate'),
                text: t('Please add content in the current language first'),
                icon: 'warning',
            });
            return;
        }
        const confirmed = await Swal.fire({
            title: t('Auto translate'),
            text: t('This will translate the current content to all other languages. Continue?'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: t('Yes, translate'),
            cancelButtonText: t('Cancel'),
        });
        if (!confirmed.isConfirmed) return;

        setTranslating(true);
        try {
            const targetLangs = languages.map((l) => l.code).filter((code) => code !== activeLang);
            const newName = { ...data.name };
            const newTitle = { ...data.title };
            const newDescription = { ...data.description };
            const newKeywords = { ...data.keywords };

            for (const toLang of targetLangs) {
                if (nameText) {
                    const translated = await translateText(nameText, activeLang, toLang);
                    if (translated) newName[toLang] = translated;
                    await new Promise((r) => setTimeout(r, 120));
                }
                if (titleText) {
                    const translated = await translateText(titleText, activeLang, toLang);
                    if (translated) newTitle[toLang] = translated;
                    await new Promise((r) => setTimeout(r, 120));
                }
                if (descText) {
                    const translated = await translateText(descText, activeLang, toLang);
                    if (translated) newDescription[toLang] = translated;
                    await new Promise((r) => setTimeout(r, 120));
                }
                if (keywordsText) {
                    const translated = await translateText(keywordsText, activeLang, toLang);
                    if (translated) newKeywords[toLang] = translated;
                    await new Promise((r) => setTimeout(r, 120));
                }
            }
            setData({
                ...data,
                name: newName,
                title: newTitle,
                description: newDescription,
                keywords: newKeywords,
            });
            Swal.fire({
                title: t('Translation completed'),
                text: t('Content has been translated to all other languages'),
                icon: 'success',
                timer: 2000,
                showConfirmButton: false,
            });
        } catch (err: any) {
            Swal.fire({
                title: t('Translation failed'),
                text: err?.message || t('An error occurred during translation'),
                icon: 'error',
            });
        } finally {
            setTranslating(false);
        }
    };

    const inputStyle = "py-3 px-5 block w-full border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:pointer-events-none bg-white text-gray-900";

    const breadcrump = [
        { name: 'Dashboard', url: '/dashboard' },
        { name: 'Blog', url: '/dashboard/Blog' },
        { name: 'List Article', url: '/dashboard/Blog/Articles' },
        { name: 'Edit Post', url: null },
    ];

    // Load languages
    const loadLanguages = async (): Promise<Array<{ code: LanguageCode; label: string }>> => {
        try {
            const res = await axios.get(`${config.url}/v1/language`);
            const languageList = res.data.message || [];
            const mappedLanguages = languageList
                .filter((lang: any) => lang.active)
                .sort((a: any, b: any) => (a.arranged || 0) - (b.arranged || 0))
                .map((lang: any) => ({
                    code: lang.code as LanguageCode,
                    label: lang.label
                }));
            setLanguages(mappedLanguages);
            if (mappedLanguages.length > 0) {
                setActiveLang(mappedLanguages[0].code);
            }
            return mappedLanguages;
        } catch (error) {
            console.error('Error loading languages:', error);
            const fallbackLanguages = [
                { code: 'ku' as LanguageCode, label: 'Kurdish' },
                { code: 'fa' as LanguageCode, label: 'فارسی' },
                { code: 'en' as LanguageCode, label: 'English' },
                { code: 'ar' as LanguageCode, label: 'العربية' },
                { code: 'tr' as LanguageCode, label: 'Türkçe' }
            ];
            setLanguages(fallbackLanguages);
            return fallbackLanguages;
        }
    };

    // Helper function to initialize multilingual field
    const initializeMultilingualField = (field: any, languageCodes: string[]): MultilingualField => {
        if (!field) {
            const obj: MultilingualField = {};
            languageCodes.forEach(code => obj[code] = '');
            return obj;
        }
        if (typeof field === 'string') {
            const obj: MultilingualField = {};
            languageCodes.forEach(code => obj[code] = field);
            return obj;
        }
        if (typeof field === 'object') {
            if (field.nl) {
                field.ku = field.nl;
                delete field.nl;
            }
            const obj: MultilingualField = { ...field };
            languageCodes.forEach(code => {
                if (!obj[code]) obj[code] = '';
            });
            return obj;
        }
        const obj: MultilingualField = {};
        languageCodes.forEach(code => obj[code] = '');
        return obj;
    };

    const loadData = async (languageCodes: string[] = []) => {
        setLoading(true);
        try {
            const res = await axios.get(`${config.url}/v1/dashboard/post/${Id}`, {
                headers: { Authorization: 'Bearer ' + cookie['token'] }
            });
            const loadedData = res.data.message;
            const codes = languageCodes.length > 0 
                ? languageCodes
                : ['ku', 'fa', 'en', 'ar', 'tr'];
            
            // Ensure multilingual structure exists
            if (loadedData) {
                loadedData.name = initializeMultilingualField(loadedData.name, codes);
                loadedData.url = initializeMultilingualField(loadedData.url, codes);
                loadedData.title = initializeMultilingualField(loadedData.title, codes);
                loadedData.description = initializeMultilingualField(loadedData.description, codes);
                loadedData.keywords = initializeMultilingualField(loadedData.keywords, codes);
            }
            setData(loadedData);
        } catch (err: any) {
            Swal.fire({
                icon: 'error',
                title: t('Error'),
                text: err.response?.data?.message || t('Error loading post'),
            });
        } finally {
            setLoading(false);
        }
    };

    const loadGroups = async () => {
        try {
            const res = await axios.get(`${config.url}/v1/dashboard/bloggroup`, {
                headers: { Authorization: 'Bearer ' + cookie['token'] }
            });
            setListArticle(res.data.message);
        } catch (err) {
            console.error('Error loading groups:', err);
        }
    };

    const save = async () => {
        setSaving(true);
        setError(null);
        try {
            await axios.put(`${config.url}/v1/dashboard/post/${Id}`, data, {
                headers: { Authorization: 'Bearer ' + cookie['token'] }
            });
            Swal.fire({
                icon: 'success',
                title: t('Success'),
                text: t('Post updated successfully'),
                timer: 1500,
                showConfirmButton: false
            });
            setKey(prev => prev + 1);
        } catch (err: any) {
            setError(err.response?.data?.message?.errors || null);
            Swal.fire({
                icon: 'error',
                title: t('Error'),
                text: err.response?.data?.message || t('Error updating post'),
            });
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        const initialize = async () => {
            const loadedLanguages = await loadLanguages();
            const languageCodes = loadedLanguages.length > 0
                ? loadedLanguages.map(l => l.code)
                : ['ku', 'fa', 'en', 'ar', 'tr'];
            await Promise.all([loadGroups(), loadData(languageCodes)]);
        };
        initialize();
    }, []);

    // Update data when languages change
    useEffect(() => {
        if (languages.length > 0 && data) {
            const languageCodes = languages.map(l => l.code);
            const updatedData = { ...data };
            updatedData.name = initializeMultilingualField(data.name, languageCodes);
            updatedData.url = initializeMultilingualField(data.url, languageCodes);
            updatedData.title = initializeMultilingualField(data.title, languageCodes);
            updatedData.description = initializeMultilingualField(data.description, languageCodes);
            updatedData.keywords = initializeMultilingualField(data.keywords, languageCodes);
            setData(updatedData);
        }
    }, [languages]);

    if (loading) {
        return (
            <div className="bg-gray-50 min-h-screen p-4 md:p-6">
                <BreadCrump data={breadcrump} />
                <HeaderOfPage name="Edit Post" />
                <div className="bg-white rounded-xl shadow-sm p-12 mt-6">
                    <div className="flex flex-col items-center justify-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                        <p className="text-gray-600 font-medium">در حال بارگذاری...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="bg-gray-50 min-h-screen p-4 md:p-6">
                <BreadCrump data={breadcrump} />
                <HeaderOfPage name="Edit Post" />
                <div className="bg-white rounded-xl shadow-sm p-12 mt-6">
                    <p className="text-center text-gray-500">پستی یافت نشد</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gray-50 min-h-screen p-4 md:p-6">
            <BreadCrump data={breadcrump} />
            <HeaderOfPage name="Edit Post" />

            <div className="mt-6">
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <Tabs defaultActiveKey="1" appearance="pills">
                        {/* Content Tab */}
                        <Tabs.Tab eventKey="1" title={t('متن')}>
                            <div className="p-6">
                                <TissEditor component="post" parent={data.id} />
                            </div>
                        </Tabs.Tab>

                        {/* Settings Tab */}
                        <Tabs.Tab eventKey="2" title={t('تنظیمات')}>
                            <div className="p-6" dir="rtl">
                                <div className="space-y-6">
                                    {/* Language Selector + Auto Translate */}
                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('Language')}
                                        </label>
                                        <div className="flex flex-wrap items-center gap-2">
                                            {languages.map((lang) => (
                                                <button
                                                    key={lang.code}
                                                    type="button"
                                                    onClick={() => setActiveLang(lang.code)}
                                                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                                        activeLang === lang.code
                                                            ? 'bg-blue-600 text-white'
                                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                    }`}
                                                >
                                                    {lang.label}
                                                </button>
                                            ))}
                                            <button
                                                type="button"
                                                onClick={handleAutoTranslate}
                                                disabled={translating}
                                                title={t('Auto translate to all other languages')}
                                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-green-600 text-green-700 bg-green-50 hover:bg-green-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                            >
                                                {translating ? (
                                                    <>
                                                        <span className="animate-spin inline-block w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full" />
                                                        <span>{t('Translating...')}</span>
                                                    </>
                                                ) : (
                                                    <span>{t('Auto translate to all other languages')}</span>
                                                )}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Basic Information */}
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-800 mb-4">اطلاعات پایه</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Code <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={data.code || ''}
                                                    className={`${inputStyle} ${error?.code ? 'border-red-500 bg-red-50' : ''}`}
                                                    onChange={e => setData({ ...data, code: e.target.value })}
                                                    placeholder="مثال: POST001"
                                                />
                                                {error?.code && <p className="mt-1 text-sm text-red-600">{error.code.message}</p>}
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    {t('Name')} ({languages.find(l => l.code === activeLang)?.label}) <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={data.name?.[activeLang] || ''}
                                                    className={`${inputStyle} ${error?.name ? 'border-red-500 bg-red-50' : ''}`}
                                                    onChange={e => {
                                                        const nameValue = e.target.value;
                                                        const slugValue = String(changeUrl(nameValue));
                                                        setData({ 
                                                            ...data, 
                                                            name: { 
                                                                ...data.name, 
                                                                [activeLang]: nameValue 
                                                            },
                                                            url: {
                                                                ...data.url,
                                                                [activeLang]: slugValue
                                                            }
                                                        });
                                                    }}
                                                    placeholder="عنوان پست"
                                                />
                                                {error?.name && <p className="mt-1 text-sm text-red-600">{error.name.message}</p>}
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    URL ({languages.find(l => l.code === activeLang)?.label}) <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={data.url?.[activeLang] || ''}
                                                    className={`${inputStyle} ${error?.url ? 'border-red-500 bg-red-50' : ''}`}
                                                    onChange={e => setData({ 
                                                        ...data, 
                                                        url: { 
                                                            ...data.url, 
                                                            [activeLang]: e.target.value 
                                                        } 
                                                    })}
                                                    placeholder="slug"
                                                />
                                                {error?.url && <p className="mt-1 text-sm text-red-600">{error.url.message}</p>}
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    {t('Group')}
                                                </label>
                                                <select
                                                    value={data.parent || ''}
                                                    onChange={e => setData({ ...data, parent: e.target.value })}
                                                    className={inputStyle}
                                                >
                                                    <option value="">{t('Choice Group')}...</option>
                                                    {list?.map((item: any) => (
                                                        <option key={item.id} value={item.id}>{getDisplayName(item.name)}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    {t('نوع پخش')}
                                                </label>
                                                <select
                                                    value={data.listmode || 0}
                                                    onChange={e => setData({ ...data, listmode: e.target.value })}
                                                    className={inputStyle}
                                                >
                                                    <option value={0}>{t('مدل 1')}</option>
                                                    <option value={1}>{t('مدل 2')}</option>
                                                    <option value={2}>{t('مدل 3')}</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Save Button */}
                                    <div className="flex justify-end pt-4 border-t border-gray-200">
                                        <Button
                                            appearance="primary"
                                            onClick={save}
                                            disabled={saving}
                                            className="px-8"
                                        >
                                            {saving ? (
                                                <>
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white inline-block ml-2"></div>
                                                    در حال ذخیره...
                                                </>
                                            ) : (
                                                <>
                                                    <svg className="w-4 h-4 inline-block ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                    {t('Update Post')}
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </Tabs.Tab>

                        {/* SEO Tab */}
                        <Tabs.Tab eventKey="3" title={t('سئو')}>
                            <div className="p-6" dir="rtl">
                                <div className="space-y-4">
                                    {/* Language Selector (shared with Settings tab) */}
                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('Language')}
                                        </label>
                                        <div className="flex flex-wrap items-center gap-2">
                                            {languages.map((lang) => (
                                                <button
                                                    key={lang.code}
                                                    type="button"
                                                    onClick={() => setActiveLang(lang.code)}
                                                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                                        activeLang === lang.code
                                                            ? 'bg-blue-600 text-white'
                                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                    }`}
                                                >
                                                    {lang.label}
                                                </button>
                                            ))}
                                            <button
                                                type="button"
                                                onClick={handleAutoTranslate}
                                                disabled={translating}
                                                title={t('Auto translate to all other languages')}
                                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-green-600 text-green-700 bg-green-50 hover:bg-green-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                            >
                                                {translating ? (
                                                    <>
                                                        <span className="animate-spin inline-block w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full" />
                                                        <span>{t('Translating...')}</span>
                                                    </>
                                                ) : (
                                                    <span>{t('Auto translate to all other languages')}</span>
                                                )}
                                            </button>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            تیتر ({languages.find(l => l.code === activeLang)?.label})
                                        </label>
                                        <Input
                                            value={data.title?.[activeLang] || ''}
                                            className={inputStyle}
                                            onChange={(e) => setData({ 
                                                ...data, 
                                                title: { 
                                                    ...data.title, 
                                                    [activeLang]: e 
                                                } 
                                            })}
                                            placeholder="تیتر صفحه"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('Keywords')} ({languages.find(l => l.code === activeLang)?.label})
                                        </label>
                                        <Input
                                            as={'textarea'}
                                            rows={3}
                                            value={data.keywords?.[activeLang] || ''}
                                            className={inputStyle}
                                            onChange={(e) => setData({ 
                                                ...data, 
                                                keywords: { 
                                                    ...data.keywords, 
                                                    [activeLang]: e 
                                                } 
                                            })}
                                            placeholder="کلمات کلیدی (با کاما جدا کنید)"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('Description')} ({languages.find(l => l.code === activeLang)?.label})
                                        </label>
                                        <Input
                                            as={`textarea`}
                                            rows={4}
                                            value={data.description?.[activeLang] || ''}
                                            className={inputStyle}
                                            onChange={(e) => setData({ 
                                                ...data, 
                                                description: { 
                                                    ...data.description, 
                                                    [activeLang]: e 
                                                } 
                                            })}
                                            placeholder="توضیحات متا"
                                        />
                                    </div>

                                    <div className="flex justify-end pt-4 border-t border-gray-200">
                                        <Button
                                            appearance="primary"
                                            onClick={save}
                                            disabled={saving}
                                            className="px-8"
                                        >
                                            {saving ? 'در حال ذخیره...' : 'ذخیره سازی'}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </Tabs.Tab>

                        {/* Images Tab */}
                        <Tabs.Tab eventKey="4" title={t('تصاویر')}>
                            <div className="p-6" dir="rtl">
                                <div className="space-y-6">
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-800 mb-4">تصاویر پست</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                                <label className="block text-sm font-medium text-gray-700 mb-3">
                                                    {t('Thumbnail')}
                                                </label>
                                                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                                                    <ImageUploader id={data.id} component="Gallery" />
                                                </div>
                                            </div>

                                            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                                <label className="block text-sm font-medium text-gray-700 mb-3">
                                                    {t('دارک مد')}
                                                </label>
                                                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                                                    <ImageUploader id={data.id} component="Postdark" />
                                                </div>
                                            </div>

                                            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                                <label className="block text-sm font-medium text-gray-700 mb-3">
                                                    {t('هدر صفحه')}
                                                </label>
                                                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                                                    <ImageUploader id={data.id} component="PostHeader" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Tabs.Tab>

                        {/* QA Tab */}
                        <Tabs.Tab eventKey="5" title={t('پرسش پاسخ')}>
                            <div className="p-6">
                                <QACreator parent={data.id} />
                            </div>
                        </Tabs.Tab>
                    </Tabs>
                </div>
            </div>
        </div>
    );
}
